import { useState, useEffect, useCallback } from 'react';
import { getSessions } from '../firebase';
import type { Session } from '../types';
import { formatDuration, localDateKey } from '../timer';

export type ReportRange = 'day' | 'week' | 'month' | 'year';

export interface DailyStats {
  date: string;          // YYYY-MM-DD
  focusSeconds: number;
  sessions: number;
  completedTasks: number;
}

export interface ReportData {
  totalFocusSeconds: number;
  totalSessions: number;
  daily: DailyStats[];
  byProject: { projectId: string; seconds: number }[];
  streak: number;
}

const dateKey = (ts: number): string => localDateKey(ts);
const HISTORY_DAYS = 90;

function aggregateDaily(sessions: Session[]): DailyStats[] {
  const dailyMap = new Map<string, DailyStats>();
  for (const s of sessions) {
    if (s.type !== 'work' || !s.completed) continue;
    const k = dateKey(s.startedAt);
    const cur = dailyMap.get(k) ?? { date: k, focusSeconds: 0, sessions: 0, completedTasks: 0 };
    cur.focusSeconds += s.durationSeconds;
    cur.sessions += 1;
    dailyMap.set(k, cur);
  }
  return Array.from(dailyMap.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function getRangeMs(range: ReportRange): [number, number] {
  const now = new Date();
  const end = now.getTime();
  switch (range) {
    case 'day':   { const s = new Date(now); s.setHours(0,0,0,0); return [s.getTime(), end]; }
    case 'week':  { const s = new Date(now); s.setDate(now.getDate() - 6); s.setHours(0,0,0,0); return [s.getTime(), end]; }
    case 'month': { const s = new Date(now); s.setDate(1); s.setHours(0,0,0,0); return [s.getTime(), end]; }
    case 'year':  { const s = new Date(now); s.setMonth(0,1); s.setHours(0,0,0,0); return [s.getTime(), end]; }
  }
}

export function useReports(uid?: string) {
  const [range, setRange] = useState<ReportRange>('week');
  const [sessions, setSessions] = useState<Session[]>([]);
  const [history, setHistory] = useState<DailyStats[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const [from, to] = getRangeMs(range);
      setSessions(await getSessions(uid, from, to));
    } catch (e) {
      console.warn('[reports] no se pudieron cargar las sesiones', e);
    } finally {
      setLoading(false);
    }
  }, [uid, range]);

  // La racha y el mapa de calor miran siempre los últimos 90 días, elijas el rango que elijas
  const fetchHistory = useCallback(async () => {
    if (!uid) return;
    const from = new Date(); from.setDate(from.getDate() - HISTORY_DAYS); from.setHours(0, 0, 0, 0);
    try { setHistory(aggregateDaily(await getSessions(uid, from.getTime(), Date.now()))); }
    catch (e) { console.warn('[reports] no se pudo cargar el historial', e); }
  }, [uid]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  const refetch = useCallback(() => { fetch(); fetchHistory(); }, [fetch, fetchHistory]);

  // Aggregate
  const workSessions = sessions.filter(s => s.type === 'work' && s.completed);
  const totalFocusSeconds = workSessions.reduce((a, s) => a + s.durationSeconds, 0);

  const daily = aggregateDaily(workSessions);

  const projectMap = new Map<string, number>();
  for (const s of workSessions) {
    if (s.projectId) {
      projectMap.set(s.projectId, (projectMap.get(s.projectId) ?? 0) + s.durationSeconds);
    }
  }
  const byProject = Array.from(projectMap.entries()).map(([projectId, seconds]) => ({ projectId, seconds }));

  return {
    range, setRange, loading, refetch,
    totalFocusSeconds, totalSessions: workSessions.length,
    daily, byProject, history,
    totalFocusFormatted: formatDuration(totalFocusSeconds),
  };
}
